import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'
// import { Course } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { Save, ArrowLeft } from 'lucide-react'

export function CourseFormPage() {
  useDocumentTitle('講座編集')
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentTenant, memberships } = useTenant()
  const [loading, setLoading] = useState(!!courseId)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ambassador_name: '',
    access_policy: 'relative_days' as 'relative_days' | 'fixed_range',
    access_days: 90,
    access_start_date: '',
    access_end_date: '',
    is_published: false,
  })

  useEffect(() => {
    const isTenantAdmin = memberships.some(
      (m) => m.tenant_id === currentTenant?.tenant_id && m.role_in_tenant === 'tenant_admin'
    )

    if (!isTenantAdmin && memberships.length > 0) {
      navigate('/mypage', { replace: true })
      return
    }

    if (courseId && user && currentTenant) {
      fetchCourse()
    }
  }, [courseId, user, currentTenant, memberships])

  const fetchCourse = async () => {
    if (!courseId || !currentTenant) return

    try {
      setLoading(true)
      setError(null)

      const { data, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('course_id', courseId)
        .eq('tenant_id', currentTenant.tenant_id)
        .single()

      if (courseError) throw courseError

      if (data) {
        setFormData({
          title: data.title,
          description: data.description || '',
          ambassador_name: data.ambassador_name || '',
          access_policy: data.access_policy,
          access_days: data.access_days || 90,
          access_start_date: data.access_start_date || '',
          access_end_date: data.access_end_date || '',
          is_published: data.is_published,
        })
      }
    } catch (err: any) {
      console.error('Error fetching course:', err)
      setError(err.message || '講座の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!currentTenant) return

    try {
      setSaving(true)
      setError(null)

      const courseData = {
        tenant_id: currentTenant.tenant_id,
        title: formData.title,
        description: formData.description || null,
        ambassador_name: formData.ambassador_name || null,
        access_policy: formData.access_policy,
        access_days: formData.access_policy === 'relative_days' ? formData.access_days : null,
        access_start_date: formData.access_policy === 'fixed_range' ? formData.access_start_date : null,
        access_end_date: formData.access_policy === 'fixed_range' ? formData.access_end_date : null,
        is_published: formData.is_published,
      }

      if (courseId) {
        // 更新
        const { error: updateError } = await supabase
          .from('courses')
          .update(courseData)
          .eq('course_id', courseId)

        if (updateError) throw updateError
      } else {
        // 新規作成
        const { data, error: insertError } = await supabase
          .from('courses')
          .insert([courseData])
          .select()
          .single()

        if (insertError) throw insertError

        // 試験も自動作成
        if (data) {
          await supabase.from('exams').insert([
            {
              course_id: data.course_id,
              tenant_id: currentTenant.tenant_id,
              title: `${formData.title} 認定試験`,
              time_limit_minutes: 60,
              passing_score: 70,
            },
          ])
        }
      }

      navigate('/tenant/courses')
    } catch (err: any) {
      console.error('Error saving course:', err)
      setError(err.message || '講座の保存に失敗しました')
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="講座を読み込み中..." />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/tenant/courses')}
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-500"
          >
            <ArrowLeft className="h-4 w-4" />
            講座一覧へ戻る
          </button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {courseId ? '講座編集' : '新規講座作成'}
          </h1>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          {/* 基本情報 */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              講座名 <span className="text-red-600">*</span>
            </label>
            <input
              id="title"
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="例: Web開発基礎講座"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              講座説明
            </label>
            <textarea
              id="description"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="講座の内容や目標を説明してください"
            />
          </div>

          <div>
            <label htmlFor="ambassador_name" className="block text-sm font-medium text-gray-700 mb-2">
              講師名
            </label>
            <input
              id="ambassador_name"
              type="text"
              value={formData.ambassador_name}
              onChange={(e) => setFormData({ ...formData, ambassador_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="例: 山田太郎"
            />
          </div>

          {/* 受講期間設定 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              受講期間設定
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="access_policy"
                  value="relative_days"
                  checked={formData.access_policy === 'relative_days'}
                  onChange={(e) =>
                    setFormData({ ...formData, access_policy: e.target.value as any })
                  }
                />
                <span>購入日から指定日数</span>
              </label>

              {formData.access_policy === 'relative_days' && (
                <div className="ml-6">
                  <input
                    type="number"
                    min="1"
                    value={formData.access_days}
                    onChange={(e) =>
                      setFormData({ ...formData, access_days: parseInt(e.target.value) })
                    }
                    className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">日間</span>
                </div>
              )}

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="access_policy"
                  value="fixed_range"
                  checked={formData.access_policy === 'fixed_range'}
                  onChange={(e) =>
                    setFormData({ ...formData, access_policy: e.target.value as any })
                  }
                />
                <span>固定期間</span>
              </label>

              {formData.access_policy === 'fixed_range' && (
                <div className="ml-6 space-y-2">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">開始日</label>
                    <input
                      type="date"
                      value={formData.access_start_date}
                      onChange={(e) =>
                        setFormData({ ...formData, access_start_date: e.target.value })
                      }
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">終了日</label>
                    <input
                      type="date"
                      value={formData.access_end_date}
                      onChange={(e) =>
                        setFormData({ ...formData, access_end_date: e.target.value })
                      }
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ステータス */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">公開状態</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="is_published"
                  value="false"
                  checked={!formData.is_published}
                  onChange={() => setFormData({ ...formData, is_published: false })}
                />
                <span>下書き（非公開）</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="is_published"
                  value="true"
                  checked={formData.is_published}
                  onChange={() => setFormData({ ...formData, is_published: true })}
                />
                <span>公開</span>
              </label>
            </div>
          </div>

          {/* アクションボタン */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition"
            >
              <Save className="h-5 w-5" />
              {saving ? '保存中...' : courseId ? '更新する' : '作成する'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/tenant/courses')}
              disabled={saving}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
            >
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  )
}
